/**
 * The persistent indicator: while a cut is on, the screen has to look cut.
 *
 * The worst outcome of this whole feature is somebody reading a cut number as the total, and a
 * selector holding a value at the top of a long screen does not prevent it — by the third chart
 * nobody remembers what the picker said. So the cut gets a band of its own, above everything, that
 * stays on the screen while it scrolls, says **which group** these numbers are about, says the base
 * in the payload's own two numbers ("de 40 das 214 pessoas que enviaram"), says what is **not** cut
 * and why, and carries the way out.
 *
 * **"Limpar recorte" is a button here and not only the empty entry of the picker.** Clearing has to
 * be one click from wherever the eye is, and it has to be visible from the same place that tells
 * the reader they are inside a cut.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { CUT_CSV_NOTICE, cutBaseSentence, cutHeadline } from '../../../Utils/resultsCut';
import { formatCount } from '../../../Utils/surveyFormat';
import type { SurveyResultsFilter } from '../../../Types/Surveys.types';

/**
 * Sticky, because the band is useless at the moment it stops being read.
 *
 * It sits above the cards and stays there while the questions scroll past, which is the only way
 * a number three screens down is still visibly part of a cut.
 */
const bandStyle = css`
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: flex-start;
  gap: var(--space-s);
  padding: var(--space-m) var(--space-ml);
  border: 1px solid var(--color-primary);
  border-left: 3px solid var(--color-primary);
  border-radius: var(--radius-card);
  background: var(--color-primary-subtle);
  text-wrap: pretty;
`;

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

const headlineStyle = css`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
`;

const baseStyle = css`
  font-size: var(--font-size-xs);
`;

const noticeStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

export interface ResultsCutNoticeProps {
  id: string;
  filter: SurveyResultsFilter;
  /** Which neighbours stay on the full base — the results screen and the adoption block differ. */
  fullBaseNotice: string;
  /** Only where an export button shares the screen: the file comes out whole. */
  withCsvNotice?: boolean;
  onClear: () => void;
}

export const ResultsCutNotice = ({
  id,
  filter,
  fullBaseNotice,
  withCsvNotice = false,
  onClear,
}: ResultsCutNoticeProps): JSX.Element => (
  <div id={id} className={bandStyle} role='status'>
    <GLIcon variant='Users.UserFocus' size={16} fill='var(--color-primary)' />
    <div className={bodyStyle}>
      <span className={headlineStyle}>Recorte aplicado · {cutHeadline(filter)}</span>
      <span className={baseStyle}>
        {filter.people === 0
          ? `Ninguém marcou essa resposta: 0 das ${formatCount(filter.totalSubmitted)} pessoas que enviaram.`
          : `Cada número por pergunta abaixo é ${cutBaseSentence(filter)}.`}
      </span>
      <p className={noticeStyle}>{fullBaseNotice}</p>
      {withCsvNotice ? <p className={noticeStyle}>{CUT_CSV_NOTICE}</p> : null}
    </div>
    <UTButton
      fitContent
      variant='secondary'
      size='small'
      id={`${id}-clear`}
      text='Limpar recorte'
      icon='Arrows.X'
      onClick={onClear}
    />
  </div>
);
